import { FC } from "react";
import { Container, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import {
  catalogListPath,
  municipalQueryFromCodes,
  profileListHref,
  withMunicipalQuery,
} from "../../constants/profile-links";

type CardVariantColor = "green" | "yellow" | "blue" | "red";

type CardProps = {
  id: string;
  title: string;
  description: string;
  href: string;
  variant?: CardVariantColor;
};

const Card = ({
  id,
  description,
  href,
  title,
  variant = "green",
}: CardProps) => {
  const variantColorMap: Record<CardVariantColor, string> = {
    blue: "var(--xvia-color-primary)",
    green: "var(--xvia-color-primary)",
    yellow: "var(--xvia-color-primary)",
    red: "var(--xvia-color-primary)",
  };

  return (
    <Stack
      id={id}
      direction="column"
      gap={3}
      sx={{
        width: "100%",
        height: "auto",
        overflow: "hidden",
        borderRadius: "7px",
        backgroundColor: "white",
        boxShadow: "1px 2px 4px 0px #ABABAB1A",
        border: "1px solid #E0E3EB",
      }}
    >
      <Box
        sx={{
          padding: "12px",
          backgroundColor: variantColorMap[variant],
          width: "100%",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: "700",
            lineHeight: "20px",
            textAlign: "left",
            color: "white",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Stack
        direction="column"
        sx={{ paddingX: "12px", paddingBottom: "16px", width: "100%" }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: "400",
            lineHeight: "16px",
            textAlign: "left",
            color: "var(--xvia-color-text-secondary)",
          }}
        >
          {description}
        </Typography>
      </Stack>
      <Stack
        direction="column"
        sx={{
          paddingX: "12px",
          paddingBottom: "16px",
          width: "100%",
          mt: "auto",
        }}
      >
        <Typography
          component="a"
          id={`${id}-link`}
          href={href}
          aria-label={`Acessar serviços para ${title}`}
          sx={{
            fontSize: "12px",
            fontWeight: "600",
            lineHeight: "none",
            letterSpacing: "0.4px",
            textAlign: "center",
            color: "#FFF",
            background: variantColorMap[variant],
            borderRadius: "8px",
            paddingY: "6px",
            paddingX: "16px",
            transition: "all 0.3s ease-in-out",
          }}
        >
          Acessar
        </Typography>
      </Stack>
    </Stack>
  );
};

type PublicRelatedCardsProps = {
  municipalityName?: string;
  municipalityCode?: number;
};

export const PublicRelatedCards: FC<PublicRelatedCardsProps> = ({
  municipalityName,
  municipalityCode,
}) => {
  if (municipalityName) {
    const municipioSuffix =
      typeof municipalityCode === "number"
        ? municipalQueryFromCodes(municipalityCode, municipalityName)
        : "";

    return (
      <div className="xvia-user-related-widgets">
        <Container className="xvia-user-related-widgets__container">
          <Stack
            gap={5}
            direction="row"
            sx={(theme) => ({
              width: "100%",
              mt: 2,
              [theme.breakpoints.down("md")]: { flexDirection: "column" },
            })}
          >
            <Card
              id="related-card-saude"
              variant="green"
              title="Saúde"
              description="Reúne serviços voltados ao bem-estar do cidadão, como emissão de carteiras, solicitação de benefícios e agendamento de atendimentos médicos e exames."
              href={withMunicipalQuery(
                catalogListPath("saude-e-bem-estar"),
                municipioSuffix
              )}
            />
            <Card
              id="related-card-transporte"
              variant="green"
              title="Transporte"
              description="Focado em condutores e veículos, oferece ferramentas para consulta de CNH, licenciamento, infrações e informações sobre o transporte intermunicipal."
              href={withMunicipalQuery(
                catalogListPath("transporte-e-veiculos"),
                municipioSuffix
              )}
            />
            <Card
              id="related-card-cultura"
              variant="green"
              title="Cultura"
              description="Oferece acesso a informações sobre eventos, espaços culturais, lazer e serviços relacionados ao fomento da cultura e preservação do patrimônio estadual."
              href={withMunicipalQuery(
                catalogListPath("cultura-e-lazer"),
                municipioSuffix
              )}
            />
            <Card
              id="related-card-meio-ambiente"
              variant="green"
              title="Meio Ambiente"
              description="Espaço dedicado a serviços de preservação, licenciamento ambiental, informações sobre a natureza e recursos hídricos do estado."
              href={withMunicipalQuery(
                catalogListPath("ambiente-e-natureza"),
                municipioSuffix
              )}
            />
            <Card
              id="related-card-educacao"
              variant="green"
              title="Educação"
              description="Concentra serviços para estudantes e professores, como matrículas, consultas a boletins, informações escolares e acesso a programas educacionais."
              href={withMunicipalQuery(
                catalogListPath("educacao-e-escola"),
                municipioSuffix
              )}
            />
          </Stack>
        </Container>
      </div>
    );
  }

  return (
    <div className="xvia-user-related-widgets">
      <Container className="xvia-user-related-widgets__container">
        <Stack
          gap={5}
          direction="row"
          sx={(theme) => ({
            width: "100%",
            mt: 2,
            [theme.breakpoints.down("md")]: { flexDirection: "column" },
          })}
        >
          <Card
            id="related-card-cidadao"
            variant="green"
            title="Cidadão"
            description="Acesse aqui serviços direcionados às necessidades corriqueiras do cidadão, como emissão de documentos, solicitação de benefícios e agendamento de atendimentos."
            href={profileListHref("cidadao", "")}
          />
          <Card
            id="related-card-servidor"
            variant="yellow"
            title="Servidor"
            description="Acesse aqui as principais ferramentas e informações relacionadas a vida funcional do servidor, como contracheques e atualização de dados cadastrais."
            href={profileListHref("servidor", "")}
          />
          <Card
            id="related-card-empresas"
            variant="blue"
            title="Empresas"
            description="Acesse aqui tudo o que precisa para sua empresa, como informações sobre registro, licenciamento e órgãos responsáveis."
            href={profileListHref("empresa", "")}
          />
          <Card
            id="related-card-detran"
            variant="red"
            title="Detran"
            description="Acesse aqui os serviços do Departamento Estadual de Trânsito do Piauí, como emissão e renovação da CNH, licenciamento de veículos e outras informações sobre trânsito e habilitação."
            href={profileListHref("detran", "")}
          />
          <Card
            id="related-card-municipios"
            variant="blue"
            title="Municípios"
            description="Acesse aqui os serviços Municipais, como emissão e renovação da CNH, licenciamento de veículos e outras informações sobre trânsito e habilitação."
            href={profileListHref("municipios", "")}
          />
        </Stack>
      </Container>
    </div>
  );
};
